import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getImageUrl } from '../lib/api';
import { devError } from '../lib/logger';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, ChevronLeft, ChevronRight, Filter, Image as ImageIcon, Pencil, Trash2, AlertTriangle, Loader2, Download } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { GalleryTrigger } from '../components/ImageGalleryModal';

export default function ProductsPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ search: '', brand: '', category: '', status: '', deity: '' });
  const [taxonomy, setTaxonomy] = useState({ brand: [], category: [], deity: [] });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const limit = 20;

  const fetchTaxonomy = useCallback(async () => {
    try {
      const [brands, categories, deities] = await Promise.all([
        api.get('/taxonomy?type=brand'), api.get('/taxonomy?type=category'), api.get('/taxonomy?type=deity')
      ]);
      setTaxonomy({ brand: brands.data, category: categories.data, deity: deities.data });
    } catch (err) { devError('Taxonomy load failed', err); }
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filters.search) params.search = filters.search;
      if (filters.brand) params.brand = filters.brand;
      if (filters.category) params.category = filters.category;
      if (filters.status) params.status = filters.status;
      if (filters.deity) params.deity = filters.deity;
      const { data } = await api.get('/products', { params });
      setProducts(data.products);
      setTotal(data.total);
    } catch { toast.error('Failed to load products'); }
    setLoading(false);
  }, [page, filters, limit]);

  useEffect(() => { fetchTaxonomy(); }, [fetchTaxonomy]);
  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const totalPages = Math.ceil(total / limit);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value === 'all' ? '' : value }));
    setPage(1);
  };

  const openDeleteDialog = async (product) => {
    setDeleteTarget(product);
    setUsage(null);
    setUsageLoading(true);
    try {
      const { data } = await api.get(`/products/${product.id}/catalog-usage`);
      setUsage(data);
    } catch { setUsage({ count: 0, catalogs: [] }); }
    setUsageLoading(false);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/products/${deleteTarget.id}`);
      toast.success(`"${deleteTarget.product_name}" deleted`);
      setDeleteTarget(null);
      setUsage(null);
      setSelectedIds(prev => { const n = new Set(prev); n.delete(deleteTarget.id); return n; });
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete product'); }
    setDeleting(false);
  };

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const allCurrentSelected = products.length > 0 && products.every(p => selectedIds.has(p.id));
  const togglePageAll = () => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (allCurrentSelected) products.forEach(p => n.delete(p.id));
      else products.forEach(p => n.add(p.id));
      return n;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const exportSelected = async () => {
    if (selectedIds.size === 0) return;
    setExporting(true);
    try {
      const ids = Array.from(selectedIds);
      const res = await api.post('/products/export', { product_ids: ids }, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const yyyymmdd = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const a = document.createElement('a');
      a.href = url;
      a.download = `products_export_${yyyymmdd}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
      toast.success(`Exported ${ids.length} product${ids.length !== 1 ? 's' : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
    setExporting(false);
  };

  const exportFiltered = async () => {
    setExporting(true);
    try {
      const payload = { filters: {
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.brand ? { brand: filters.brand } : {}),
        ...(filters.category ? { category: filters.category } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.deity ? { deity: filters.deity } : {}),
      } };
      const res = await api.post('/products/export', payload, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const yyyymmdd = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const a = document.createElement('a');
      a.href = url;
      a.download = `products_export_${yyyymmdd}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
      toast.success('Filtered export ready');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
    setExporting(false);
  };

  return (
    <div data-testid="products-page" className="animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl text-[#1A1A1A] tracking-tight">Products</h1>
          <p className="text-sm text-[#5C5C5C] mt-1">{total} products in database</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="border-[#E5E0D8] text-[#5C5C5C] rounded-sm" data-testid="toggle-filters-btn">
            <Filter size={16} className="mr-2" />Filters
          </Button>
          <Button
            variant="outline"
            onClick={exportSelected}
            disabled={selectedIds.size === 0 || exporting}
            className="border-[#E5E0D8] text-[#5C5C5C] rounded-sm disabled:opacity-50"
            data-testid="export-selected-btn"
            title={selectedIds.size === 0 ? 'Select at least one product to export' : `Export ${selectedIds.size} selected to Excel`}
          >
            {exporting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}
            Export Selected{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
          </Button>
          <Button
            variant="outline"
            onClick={exportFiltered}
            disabled={exporting || total === 0}
            className="border-[#E5E0D8] text-[#5C5C5C] rounded-sm disabled:opacity-50"
            data-testid="export-filtered-btn"
            title={`Export all ${total} products matching current filters`}
          >
            <Download size={16} className="mr-2" />Export All ({total})
          </Button>
          <Button onClick={() => navigate('/products/new')} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="add-product-btn">
            <Plus size={16} className="mr-2" />Add Product
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
        <Input
          data-testid="product-search-input"
          placeholder="Search products by name, code, or keyword..."
          value={filters.search}
          onChange={e => handleFilterChange('search', e.target.value)}
          className="pl-10 rounded-sm border-[#E5E0D8] focus:ring-[#C5A059]"
        />
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="grid grid-cols-4 gap-4 mb-6 p-4 bg-white border border-[#E5E0D8] rounded-sm" data-testid="filters-panel">
          <Select value={filters.brand || 'all'} onValueChange={v => handleFilterChange('brand', v)}>
            <SelectTrigger className="rounded-sm border-[#E5E0D8]"><SelectValue placeholder="Brand" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Brands</SelectItem>{taxonomy.brand.map(b => <SelectItem key={b.id} value={b.value}>{b.value}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.category || 'all'} onValueChange={v => handleFilterChange('category', v)}>
            <SelectTrigger className="rounded-sm border-[#E5E0D8]"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Categories</SelectItem>{taxonomy.category.map(c => <SelectItem key={c.id} value={c.value}>{c.value}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.deity || 'all'} onValueChange={v => handleFilterChange('deity', v)}>
            <SelectTrigger className="rounded-sm border-[#E5E0D8]"><SelectValue placeholder="Deity" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Deities</SelectItem>{taxonomy.deity.map(d => <SelectItem key={d.id} value={d.value}>{d.value}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.status || 'all'} onValueChange={v => handleFilterChange('status', v)}>
            <SelectTrigger className="rounded-sm border-[#E5E0D8]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="discontinued">Discontinued</SelectItem></SelectContent>
          </Select>
        </div>
      )}

      {/* Selection summary bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between mb-3 px-4 py-2 bg-[#FEF3E2] border border-[#C5A059]/40 rounded-sm" data-testid="selection-bar">
          <p className="text-sm text-[#1A1A1A]">
            <strong>{selectedIds.size}</strong> product{selectedIds.size !== 1 ? 's' : ''} selected
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={clearSelection} className="rounded-sm border-[#E5E0D8] text-xs" data-testid="clear-selection-btn">Clear</Button>
            <Button size="sm" onClick={exportSelected} disabled={exporting} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm text-xs" data-testid="export-selected-bar-btn">
              {exporting ? <><Loader2 size={12} className="mr-1.5 animate-spin" />Exporting...</> : <><Download size={12} className="mr-1.5" />Export to Excel</>}
            </Button>
          </div>
        </div>
      )}

      {/* Product Table */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="spinner-gold" /></div>
      ) : products.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#E5E0D8] rounded-sm" data-testid="empty-products">
          <ImageIcon size={40} className="mx-auto text-[#E5E0D8] mb-4" />
          <p className="text-[#5C5C5C]">No products found</p>
          <Button onClick={() => navigate('/products/new')} className="mt-4 bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm">Add First Product</Button>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E0D8] rounded-sm overflow-hidden">
          <table className="w-full" data-testid="products-table">
            <thead>
              <tr className="border-b border-[#E5E0D8]">
                <th className="w-10 py-3 px-3">
                  <Checkbox
                    checked={allCurrentSelected}
                    onCheckedChange={togglePageAll}
                    aria-label="Select all on this page"
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="text-left py-3 px-4 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C]">Image</th>
                <th className="text-left py-3 px-4 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C]">Code</th>
                <th className="text-left py-3 px-4 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C]">Product Name</th>
                <th className="text-left py-3 px-4 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C]">Brand</th>
                <th className="text-left py-3 px-4 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C]">Category</th>
                <th className="text-right py-3 px-4 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C]">MRP</th>
                <th className="text-left py-3 px-4 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C]">Status</th>
                <th className="text-right py-3 px-4 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id} className={`border-b border-[#E5E0D8] hover:bg-[#F5F2EB] transition-colors ${selectedIds.has(p.id) ? 'bg-[#FBF7EE]' : ''}`} data-testid={`product-row-${p.id}`}>
                  <td className="py-3 px-3">
                    <Checkbox
                      checked={selectedIds.has(p.id)}
                      onCheckedChange={() => toggleOne(p.id)}
                      aria-label={`Select ${p.product_name}`}
                      data-testid={`select-product-${p.id}`}
                    />
                  </td>
                  <td className="py-3 px-4">
                    <GalleryTrigger coverImage={p.cover_image} galleryImages={p.gallery_images} className="w-10 h-10 rounded-sm" />
                  </td>
                  <td className="py-3 px-4 text-xs text-[#C5A059] tracking-wider font-medium">{p.product_code}</td>
                  <td className="py-3 px-4 text-sm font-medium text-[#1A1A1A] cursor-pointer" onClick={() => navigate(`/products/${p.id}/edit`)}>{p.product_name}</td>
                  <td className="py-3 px-4 text-sm text-[#5C5C5C]">{p.brand}</td>
                  <td className="py-3 px-4 text-sm text-[#5C5C5C]">{p.category}</td>
                  <td className="py-3 px-4 text-sm text-right font-medium text-[#1A1A1A]">{p.mrp ? `\u20B9${p.mrp}` : '-'}</td>
                  <td className="py-3 px-4">
                    <Badge variant={p.status === 'active' ? 'default' : 'secondary'} className={p.status === 'active' ? 'bg-[#2D6A4F] text-white text-[10px]' : 'bg-[#E5E0D8] text-[#5C5C5C] text-[10px]'}>{p.status}</Badge>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/products/${p.id}/edit`); }}
                        className="text-[#5C5C5C] hover:text-[#C5A059] p-1.5 rounded-sm hover:bg-[#F2E8D5] transition-colors"
                        data-testid={`edit-product-${p.id}`}
                        title="Edit product"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); openDeleteDialog(p); }}
                        className="text-[#5C5C5C] hover:text-[#9E2A2B] p-1.5 rounded-sm hover:bg-red-50 transition-colors"
                        data-testid={`delete-product-${p.id}`}
                        title="Delete product"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination — pb-24 keeps the buttons clear of the fixed "Made with Emergent" badge anchored bottom-right. */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 mb-24 px-1" data-testid="products-pagination">
          <p className="text-sm text-[#5C5C5C]">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="rounded-sm border-[#E5E0D8]" data-testid="pagination-prev"><ChevronLeft size={16} /></Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="rounded-sm border-[#E5E0D8]" data-testid="pagination-next"><ChevronRight size={16} /></Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(deleteTarget)} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setUsage(null); } }}>
        <DialogContent className="bg-white rounded-sm border-[#E5E0D8]" data-testid="delete-product-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg flex items-center gap-2">
              <Trash2 size={18} className="text-[#9E2A2B]" />
              Delete Product
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <p className="text-sm text-[#5C5C5C]">
              Are you sure you want to delete <strong className="text-[#1A1A1A]">{deleteTarget?.product_name}</strong> (<span className="text-[#C5A059]">{deleteTarget?.product_code}</span>)?
            </p>
            {usageLoading ? (
              <div className="flex items-center gap-2 text-xs text-[#5C5C5C]"><Loader2 size={14} className="animate-spin" />Checking catalog usage...</div>
            ) : usage && usage.count > 0 ? (
              <div className="p-3 bg-[#FEF3E2] border border-[#C5A059]/40 rounded-sm" data-testid="usage-warning">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="text-[#B38F48] shrink-0 mt-0.5" />
                  <div className="text-xs text-[#5C5C5C] leading-relaxed">
                    <p className="font-semibold text-[#1A1A1A] mb-1">Used in {usage.count} existing catalog{usage.count !== 1 ? 's' : ''}</p>
                    <p className="mb-2">Existing catalogs will keep this product intact. It will only be hidden from the product list and future catalog builders.</p>
                    <ul className="list-disc list-inside space-y-0.5 max-h-24 overflow-y-auto">
                      {usage.catalogs.slice(0, 6).map(c => <li key={c.id} className="truncate">{c.title} <span className="text-[#C5A059] uppercase text-[9px]">{c.status}</span></li>)}
                      {usage.catalogs.length > 6 && <li className="text-[#888]">+{usage.catalogs.length - 6} more</li>}
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-[#5C5C5C]">This product is not used in any catalog. It will be hidden from the product list.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setUsage(null); }} className="rounded-sm border-[#E5E0D8]" data-testid="cancel-delete-btn">Cancel</Button>
            <Button onClick={confirmDelete} disabled={deleting} className="bg-[#9E2A2B] hover:bg-[#832122] text-white rounded-sm" data-testid="confirm-delete-btn">
              {deleting ? <><Loader2 size={14} className="mr-2 animate-spin" />Deleting...</> : 'Delete Product'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
